import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Drawer, IconButton, TablePagination, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { NAVY, FONT } from "./formStyles";

/**
 * The table's cells stack two lines (name + surname, date + time). A card keeps them on one, so it stays small. When there
 * is not the room the second part gives way first, so the name or the vehicle itself is never squeezed out.
 */
const ONE_LINE_SX = {
  "& .MuiBox-root": { display: "flex", alignItems: "baseline", gap: "4px", maxWidth: "100% !important", minWidth: 0 },
  "& .MuiTypography-root": { lineHeight: "18px", minWidth: 0, flex: "0 1 auto" },
  "& .MuiTypography-root + .MuiTypography-root": { flex: "0 100000 auto" },
};

/**
 * A card's line: one thing on the left edge, one on the right, so the values line up from card to card. The right one keeps
 * its size (a premium or a company is never cut, a two-insurer pair stops at half the line) and the left one gives way.
 */
const Line = ({ left, right, leftSx, rightSx }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
    <Box sx={{ flex: "1 1 0", minWidth: 0, ...ONE_LINE_SX, ...leftSx }}>{left}</Box>
    <Box sx={{ flex: "0 0 auto", maxWidth: "50%", textAlign: "right", ...ONE_LINE_SX, ...rightSx }}>{right}</Box>
  </Box>
);

// The customer stands out: the surname reads as part of the name here, and the premium beside it matches.
const NAME_SX = { "& .MuiTypography-root": { fontSize: "14px !important", fontWeight: "700 !important", color: `${NAVY} !important` } };
const PREMIUM_SX = { "& .MuiTypography-root": { fontSize: "13.5px !important", fontWeight: "700 !important" } };
const QUIET_SX = { "& .MuiTypography-root": { fontSize: "11.5px !important", fontWeight: "500 !important", color: "#667085 !important" } };
// The date only: the time is on the policy's details.
const DATE_SX = { ...QUIET_SX, "& .MuiTypography-root + .MuiTypography-root": { display: "none" } };
const SMALL_SX = { "& .MuiTypography-root": { fontSize: "12px !important", fontWeight: "600 !important" } };
// The status: one line per part (OD + TP, PA), the way the table shows it (the owner), a little tighter.
const STATUS_SX = {
  mt: 0.5,
  "& > .MuiBox-root > *": { maxWidth: "100% !important" },
  "& .MuiChip-root": { height: 20, fontSize: "11px" },
};

// The table's cells cap their width for columns; a card line has the room.
const FULL_WIDTH_SX = { "& > *": { maxWidth: "100% !important" } };

// The table's action buttons (renderActions) as a list you can read and tap. They stay the SAME buttons, so every check and
// handler is the table's own: each one's title (its aria-label) is shown as the row's words, and the button's own ::before
// is stretched over the whole row so a tap on the words counts too.
const ACTION_LIST_SX = {
  "& > div": { display: "block !important" },
  "& > div > span": {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minHeight: 54,
    px: 2,
    py: 1,
    borderTop: "1px solid #F2F4F7",
    fontFamily: FONT,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "19px",
    color: NAVY,
    "&:active": { backgroundColor: "#F2F4F7" },
    "&::after": { content: "attr(aria-label)", flex: 1, minWidth: 0 },
    '&[aria-label="View issues"]::after': { color: "#B42318" },
  },
  "& .MuiIconButton-root": {
    position: "static",
    flexShrink: 0,
    width: 34,
    height: 34,
    "&::before": { content: '""', position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
  },
};

const idOf = (row, i) => row._id || row.id || i;

/**
 * The Online Policy list on a phone: one simple card per policy, and a tap on it asks which action (the owner, 2026-11-02).
 * Every piece is the SAME element the table shows (processedTableData's render* cells: the status, the action buttons with
 * their checks), so a card does exactly what a table row does; only the layout differs.
 */
const PolicyCardList = ({ rows = [], toneOf, loading = false, totalCount = 0, page = 0, rowsPerPage = 10, onPage, onRowsPerPage }) => {
  const [openId, setOpenId] = useState(null);
  const [acting, setActing] = useState(false);
  const actionsRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Looked up every time, so the sheet always offers what the row allows NOW (rows are patched while it is open). The last
  // one is kept for the moment the sheet slides away, or it would empty itself on the way down.
  const current = openId === null ? null : rows.find((row, i) => idOf(row, i) === openId) || null;
  const lastShown = useRef(null);
  if (current) lastShown.current = current;
  const shown = lastShown.current;

  const close = () => {
    setOpenId(null);
    setActing(false);
  };

  // The row it was opened for is gone from the list.
  useEffect(() => {
    if (openId !== null && !current) close();
  }, [openId, current]);

  // An action decides what comes next: it shows a spinner while it asks the server, then opens a page or a dialog. The sheet
  // waits for that (its button is disabled meanwhile), so a slow connection still shows the tap registered, and then goes.
  const onActionTap = (event) => {
    if (!event.target.closest("button")) return;
    setActing(true);
    const whenDone = () => {
      if (!alive.current) return;
      if (actionsRef.current && actionsRef.current.querySelector("button:disabled")) {
        setTimeout(whenDone, 120);
      } else {
        close();
      }
    };
    setTimeout(whenDone, 0);
  };

  return (
    <Box data-testid="policy-card-list" sx={{ fontFamily: FONT }}>
      {loading && !rows.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: NAVY }} />
        </Box>
      ) : null}
      {!loading && !rows.length ? (
        <Typography sx={{ textAlign: "center", color: "#667085", fontSize: "13px", py: 4, fontFamily: FONT }}>No policies found</Typography>
      ) : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {rows.map((row, i) => {
          // the table row's status colour: tint + bar on the left (rowTone; a card has no hover)
          const tone = toneOf ? toneOf(row) : null;
          const id = idOf(row, i);
          return (
            <Box
              key={id}
              data-testid="policy-card"
              role="button"
              tabIndex={0}
              aria-label={`Actions for ${row.fullName || row.firstName || "this policy"}`}
              onClick={() => setOpenId(id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpenId(id);
                }
              }}
              sx={{
                position: "relative",
                border: "1px solid #EAECF0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                transition: "background-color 0.12s, transform 0.12s",
                "&:active": { transform: "scale(0.99)", backgroundColor: "#F9FAFB" },
                "&:focus-visible": { outline: `2px solid ${NAVY}`, outlineOffset: 1 },
                ...(tone ? { backgroundColor: tone.bg, borderLeft: `4px solid ${tone.bar}` } : null),
              }}
            >
              <Box sx={{ pl: 1.5, pr: 3.25, py: 1 }}>
                <Line left={row.renderCustomerName} right={row.renderPremium} leftSx={NAME_SX} rightSx={PREMIUM_SX} />
                <Line left={row.renderPolicyRef} right={row.renderCreatedAt} leftSx={QUIET_SX} rightSx={DATE_SX} />
                <Line left={row.renderMakeModel} right={row.renderCompany} leftSx={SMALL_SX} rightSx={SMALL_SX} />
                <Box sx={STATUS_SX}>{row.renderJobStatus}</Box>
              </Box>

              {/* says the card can be tapped */}
              <ChevronRightRoundedIcon
                sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", fontSize: 20, color: "#98A2B3" }}
              />
            </Box>
          );
        })}
      </Box>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onPageChange={(e, next) => onPage && onPage(next)}
        onRowsPerPageChange={(e) => onRowsPerPage && onRowsPerPage(parseInt(e.target.value, 10))}
        labelRowsPerPage="Rows"
        sx={{
          mt: 1,
          "& .MuiTablePagination-toolbar": { px: 0, minHeight: 44, flexWrap: "wrap", justifyContent: "flex-end" },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontFamily: FONT, fontSize: "12px", color: "#475467" },
        }}
      />

      <Drawer
        anchor="bottom"
        open={!!current}
        onClose={close}
        PaperProps={{
          "data-testid": "policy-actions",
          sx: {
            borderRadius: "16px 16px 0 0",
            maxWidth: 520,
            mx: "auto",
            maxHeight: "85vh",
            overflowX: "hidden",
            pb: "env(safe-area-inset-bottom)",
          },
        }}
      >
        {shown ? (
          <>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#D0D5DD", mx: "auto", mt: 1 }} />
            {/* who, then which policy, then the status: each on a line of its own, so none of them squeezes another */}
            <Box sx={{ pl: 2, pr: 1, pt: 0.5, pb: 1.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, ...ONE_LINE_SX, ...NAME_SX }}>{shown.renderCustomerName}</Box>
                <IconButton aria-label="Close" size="small" onClick={close} sx={{ flexShrink: 0, color: "#667085" }}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ pr: 1, ...ONE_LINE_SX, ...QUIET_SX }}>{shown.renderPolicyRef}</Box>
              <Box sx={{ pr: 1, ...STATUS_SX, mt: 0.75 }}>{shown.renderJobStatus}</Box>
            </Box>
            <Box
              ref={actionsRef}
              onClick={onActionTap}
              sx={{ ...ACTION_LIST_SX, overflowX: "hidden", overflowY: "auto", ...(acting ? { pointerEvents: "none" } : null) }}
            >
              {shown.renderActions}
            </Box>
          </>
        ) : null}
      </Drawer>
    </Box>
  );
};

export default PolicyCardList;
